// fundFilter.slice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fundFilterService } from './fundFilter.service';
import { setAlertError, setAlertSuccess } from '../alert/alert.slice';

const initialState = {
  vehicle: null,
  funds: [],
  helpDialog: null,

  loading: false,
  updating: false,
  success: false,
  error: null
};

// --- CREATE ASYNC ---
export const createFundFilterAsync = createAsyncThunk(
  'fundFilter/createFundFilter',
  async (fundFilterData, thunkAPI) => {
    try {
      const res = await fundFilterService.create(fundFilterData);
      thunkAPI.dispatch(setAlertSuccess('Filter created'));
      return res.data.fundFilterer;
    } catch (error) {
      const err = error.response?.data || { msg: 'Failed to create filter' };
      thunkAPI.dispatch(setAlertError(err.msg));
      return thunkAPI.rejectWithValue(err.msg);
    }
  }
);

// --- UPDATE ASYNC ---
export const updateFundFilterAsync = createAsyncThunk(
  'fundFilter/updateFundFilter',
  async (fundFilterData, thunkAPI) => {
    try {
      const res = await fundFilterService.update(fundFilterData);
      thunkAPI.dispatch(setAlertSuccess('Filter updated'));
      return res.data.fundFilterer;
    } catch (error) {
      const err = error.response?.data || { msg: 'Failed to update filter' };
      thunkAPI.dispatch(setAlertError(err.msg));
      return thunkAPI.rejectWithValue(err.msg);
    }
  }
);

// --- DOWNLOAD PDF ASYNC ---
export const downloadPdf = createAsyncThunk('fundFilter/downloadPdf', async (id, thunkAPI) => {
  try {
    await fundFilterService.downloadPdf(id);
    thunkAPI.dispatch(setAlertSuccess('PDF downloaded'));
  } catch (error) {
    const err = error.response?.data || { msg: 'Error downloading PDF' };
    thunkAPI.dispatch(setAlertError(err.msg));
    return thunkAPI.rejectWithValue(err.msg);
  }
});

export const fundFilterSlice = createSlice({
  name: 'fundFilter',
  initialState,
  reducers: {
    // --- RESET ---
    resetFundFilter: (state) => {
      Object.assign(state, initialState);
    },

    // --- PLAIN CREATE ---
    createFundFilter: (state, action) => {
      Object.assign(state, action.payload);
      state.success = true;
    },

    // --- PLAIN UPDATE ---
    updateFundFilter: (state, action) => {
      Object.assign(state, action.payload);
      state.updating = false;
      state.success = true;
    }
  },
  extraReducers: (builder) => {
    builder
      // --- CREATE ASYNC ---
      .addCase(createFundFilterAsync.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createFundFilterAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        Object.assign(state, action.payload);
      })
      .addCase(createFundFilterAsync.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload;
      })

      // --- UPDATE ASYNC ---
      .addCase(updateFundFilterAsync.pending, (state) => {
        state.updating = true;
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(updateFundFilterAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.updating = false;
        state.success = true;
        Object.assign(state, action.payload);
      })
      .addCase(updateFundFilterAsync.rejected, (state, action) => {
        state.loading = false;
        state.updating = false;
        state.success = false;
        state.error = action.payload;
      })

      // --- DOWNLOAD PDF ---
      .addCase(downloadPdf.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(downloadPdf.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(downloadPdf.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

// --- EXPORTS ---
export const { resetFundFilter, createFundFilter, updateFundFilter } = fundFilterSlice.actions;

export default fundFilterSlice.reducer;
